import { z } from 'zod/v4';
import { SubscriptionStatus, AccessTargetType } from '@masters-club/types';

// ==================== Enums as Zod Schemas ====================

export const subscriptionStatusSchema = z.nativeEnum(SubscriptionStatus);
export const accessTargetSchema = z.nativeEnum(AccessTargetType);

// ==================== Subscription Benefit Schemas ====================
// Note: Subscription Plan schemas are in subscription-plans.ts

export const createBenefitSchema = z.object({
  order: z.number().int('Порядок должен быть целым числом').default(0),
  translations: z
    .array(
      z.object({
        languageCode: z.string().length(2, 'Код языка должен быть 2 символа'),
        text: z.string()
          .min(1, 'Текст преимущества обязателен')
          .max(200, 'Текст преимущества слишком длинный (максимум 200 символов)'),
      })
    )
    .min(1, 'Требуется хотя бы один перевод'),
});

export const updateBenefitSchema = z.object({
  order: z.number().int('Порядок должен быть целым числом').optional(),
  translations: z
    .array(
      z.object({
        languageCode: z.string().length(2, 'Код языка должен быть 2 символа'),
        text: z.string()
          .min(1, 'Текст преимущества обязателен')
          .max(200, 'Текст преимущества слишком длинный (максимум 200 символов)'),
      })
    )
    .optional(),
});

export const reorderBenefitsSchema = z.object({
  benefitIds: z.array(z.string().uuid()),
});

// ==================== Subscription Schemas ====================

export const createSubscriptionSchema = z.object({
  userId: z.number().int('ID пользователя должен быть целым числом').positive('ID пользователя должен быть положительным'),
  subscriptionPlanId: z.coerce.number().int('ID плана должен быть целым числом'),
  status: subscriptionStatusSchema.default(SubscriptionStatus.PENDING),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  sourceOrderId: z.coerce.number().int('ID заказа должен быть целым числом').optional(),
  sourcePaymentId: z.coerce.number().int('ID платежа должен быть целым числом').optional(),
});

export const updateSubscriptionStatusSchema = z.object({
  status: subscriptionStatusSchema,
});

export const extendSubscriptionSchema = z.object({
  days: z.number()
    .int('Количество дней должно быть целым числом')
    .positive('Количество дней должно быть положительным')
    .min(1, 'Минимум 1 день')
    .max(365, 'Максимум 365 дней'),
});

// Тумблер автопродления подписки (профиль/чекаут). autoRenew=false отключает
// будущие списания, доступ доживает до endDate (НЕ отмена подписки).
export const subscriptionAutoRenewSchema = z.object({
  autoRenew: z.boolean(),
});

export const subscriptionFiltersSchema = z.object({
  status: subscriptionStatusSchema.optional(),
  subscriptionPlanId: z.coerce.number().int('ID плана должен быть целым числом').optional(),
  userId: z.coerce.number().int('ID пользователя должен быть целым числом').positive('ID пользователя должен быть положительным').optional(),
  startDateFrom: z.coerce.date().optional(),
  startDateTo: z.coerce.date().optional(),
  endDateFrom: z.coerce.date().optional(),
  endDateTo: z.coerce.date().optional(),
  search: z.string().optional(),
  sortBy: z.enum(['id', 'createdAt', 'updatedAt', 'status', 'userId', 'subscriptionPlanId', 'startDate', 'endDate']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  page: z.coerce.number().int('Страница должна быть целым числом').positive('Страница должна быть положительной').default(1),
  limit: z.coerce.number().int('Лимит должен быть целым числом').positive('Лимит должен быть положительным').max(500, 'Лимит должен быть <= 500').default(20),
});

// ==================== Type Exports ====================

export type CreateBenefitDto = z.infer<typeof createBenefitSchema>;
export type UpdateBenefitDto = z.infer<typeof updateBenefitSchema>;
export type CreateSubscriptionDto = z.infer<typeof createSubscriptionSchema>;
export type UpdateSubscriptionStatusDto = z.infer<typeof updateSubscriptionStatusSchema>;
export type ExtendSubscriptionDto = z.infer<typeof extendSubscriptionSchema>;
export type SubscriptionAutoRenewDto = z.infer<typeof subscriptionAutoRenewSchema>;
export type SubscriptionFiltersDto = z.infer<typeof subscriptionFiltersSchema>;

// ==================== Response Types ====================

/**
 * Подписка с данными пользователя
 */
export interface SubscriptionWithUser {
  id: number;
  userId: number;
  subscriptionPlanId: number;
  status: SubscriptionStatus;
  startDate: Date;
  endDate: Date | null;
  sourceOrderId: number | null;
  sourcePaymentId: number | null;
  isFamilyOwner: boolean | null;
  familySubscriptionId: number | null;
  createdAt: Date;
  updatedAt: Date;
  user?: {
    id: number;
    telegramId: number;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
  };
}
